'use client';

/**
 * Warenkorb-Badge (Header oben rechts).
 *
 * - Lädt GET /api/billing/cart beim Mount + bei window-focus (KEIN Polling).
 * - Zeigt Count-Badge NUR wenn items.length > 0.
 * - Klick → /admin/company?tab=billing&sub=shop (Shop-Tab).
 * - Fehler werden still geschluckt (kein Toast). Bei Fehler/leerem Cart einfach
 *   ohne Zahl; das Icon bleibt aber als Shortcut zum Shop sichtbar.
 *
 * Stil orientiert sich an den bestehenden Topbar-Icons (Bell/Search) sowie am
 * AiUsageWidget als Referenz. Tokens only, dark+light, BackendAuth.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { ShoppingCart } from 'lucide-react';
import { BackendAuth } from '@/lib/auth-backend';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';

export default function CartBadge() {
  const router = useRouter();
  const { t } = useTranslation();
  const [count, setCount] = useState(0);
  const mountedRef = useRef(true);

  const fetchCart = useCallback(async () => {
    try {
      const data = await BackendAuth.apiCallJson('/api/billing/cart');
      const items = Array.isArray(data?.items) ? data.items : [];
      if (mountedRef.current) setCount(items.length);
    } catch {
      // still: kein Toast, Badge bleibt ohne Zahl
      if (mountedRef.current) setCount(0);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchCart();

    // KEIN Polling — nur Refresh bei window-focus
    const onFocus = () => fetchCart();
    window.addEventListener('focus', onFocus);
    return () => {
      mountedRef.current = false;
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchCart]);

  const ariaLabel = t('cart_badge.aria_label', {
    defaultValue: 'Warenkorb',
    editable: false,
  });

  return (
    <Button
      variant="ghost"
      mode="icon"
      shape="circle"
      className="relative size-9 hover:bg-primary/10 hover:[&_svg]:text-primary"
      onClick={() => router.push('/admin/company?tab=billing&sub=shop')}
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      <ShoppingCart className="size-4.5!" />
      {count > 0 && (
        <span
          className="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-semibold leading-none"
          aria-hidden="true"
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </Button>
  );
}
